import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormState, useFormApi } from 'informed';
import { AccessibleForm, Button, StepperContext, Row, Col, SelectInput, Box, Heading, InputMessage, themes, NewDateInput, Label, } from '@digitools/honest-react';
import moment from 'moment';
import styled from 'styled-components';
import { format, isWithinInterval, parse, startOfToday, subYears } from 'date-fns';
import useExperience from '../../../hooks/useExperience';
import { dateMask } from '../../eoi/utils/InputMasks';
import useTealium from '../../../hooks/useTealium';
import { TEALIUM_EVENT } from '../../../constants/tealiumConstants';
import useAuthentication from 'src/hooks/useAuthentication';
import useTranslate from 'src/hooks/useTranslate';
import { RabValidationLabels } from '../constants/RabValidationLabels';
import { CommonValidationLabels } from 'src/constants/CommonValidationLabels';
const StyledLabel = styled(Label) `
  margin: 0;
  display: inline-block;
  font-weight: bold;
  font-size: 1rem;
  & + div {
    margin-top: 10px;
  }
`;
const numberOfBirthTypes = [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
];
const hoursTypes = [
    { label: '01', value: '01' },
    { label: '02', value: '02' },
    { label: '03', value: '03' },
    { label: '04', value: '04' },
    { label: '05', value: '05' },
    { label: '06', value: '06' },
    { label: '07', value: '07' },
    { label: '08', value: '08' },
    { label: '09', value: '09' },
    { label: '10', value: '10' },
    { label: '11', value: '11' },
    { label: '12', value: '12' },
];
const amPmTypes = [{ label: 'AM', value: 'AM' }, { label: 'PM', value: 'PM' }];
const RabReportForm = (props) => {
    const { language } = useExperience();
    const formApi = useFormApi();
    const formState = useFormState();
    const { t: translation } = useTranslation('rabTranslation');
    const { t } = useTranslate();
    const isFutureTime = (time) => {
        const currentTime = moment().format('HH:mm');
        if (time) {
            const receivedTime = moment(time, 'hh:mm A').format('HH:mm');
            if (moment(receivedTime, 'HH:mm').isAfter(moment(currentTime, 'HH:mm'))) {
                return true;
            }
            return false;
        }
        return false;
    };
    const validateDateOfBirth = (value) => {
        let minDate = parse(format(subYears(new Date(), 2), 'P'), 'P', new Date());
        let maxDate = parse(format(startOfToday(), 'P'), 'P', new Date());
        let inputDate = new Date(value);
        if (!isWithinInterval(inputDate, { start: minDate, end: maxDate })) {
            return t({
                english: `Date of birth: date is not between ${format(minDate, 'MM/dd/yyyy')} and ${format(maxDate, 'MM/dd/yyyy')}`,
                spanish: `Fecha de nacimiento: la fecha no está entre ${format(minDate, 'MM/dd/yyyy')} y ${format(maxDate, 'MM/dd/yyyy')}`,
            });
        }
    };
    const validateTime = () => {
        const now = moment().format('mm/dd/yyyy');
        const target = moment(JSON.stringify(formState.values.dateOfBirth)).format('mm/dd/yyyy');
        const hours = formState.values.hours;
        const minutes = formState.values.minutes;
        let futureTime = false;
        if (hours && minutes) {
            const ampm = formState.values.ampm === 'AM' ? 'A' : 'P';
            const time = `${hours}:${minutes} ${ampm}`;
            futureTime = isFutureTime(time);
        }
        if (target === now && futureTime) {
            return true;
        }
        return false;
    };
    const validateTimeErrorText = () => {
        if (validateTime()) {
            return translation('timeCannotBeInFutureText');
        }
        return '';
    };
    useEffect(() => {
        if (formApi && validateTime()) {
            formApi.setFormError(translation('timeCannotBeInFutureText'));
        }
    }, [formState.values.hours, formState.values.minutes, formState.values.ampm]);
    const deliveryTypes = [
        { label: translation('vaginalText'), value: translation('vaginalText') },
        { label: translation('cesareanText'), value: translation('cesareanText') },
    ];
    const deliveryMultipleTypes = [
        { label: translation('yesText'), value: translation('cesareanText') },
        { label: translation('noText'), value: translation('vaginalText') },
    ];
    const bondingQuestionTypes = [
        { label: translation('yesText'), value: translation('yesText') },
        { label: translation('noText'), value: translation('noText') },
        { label: translation('unknownText'), value: translation('unknownText') },
    ];
    const createArrayOfHoursSelect = (input) => {
        const returnArray = Array(input)
            .fill(null)
            .map((value, i) => {
            if (i < 9) {
                return { label: `0${i + 1}`, value: `0${i + 1}` };
            }
            else {
                return { label: `${i + 1}`, value: `${i + 1}` };
            }
        });
        returnArray.unshift({ label: '00', value: '00' });
        return returnArray;
    };
    const bondingTypeText = () => {
        let bondingType;
        const deliveryWeeks = formState.values.deliveryType === translation('vaginalText')
            ? props.rabCustomerConfData.vaginalDeliveryWeeks.toString()
            : formState.values.deliveryType === translation('cesareanText')
                ? props.rabCustomerConfData.cesareanDeliveryWeeks.toString()
                : '';
        bondingType = translation('bondingQuestionText').replace('{deliveryWeeks}', deliveryWeeks);
        if (formState.values.numberOfBirth > 1) {
            bondingType = bondingType.replace('?', 's?');
        }
        return bondingType;
    };
    const dateFormat = ' (mm/dd/yyyy)';
    return (<>
      <div data-public={true} style={{ marginTop: '2rem' }}>
        <Heading elemType={'h2'} theme={themes.rebrand} color={'primary'} data-testid='rabReport'>
          {translation('stepLabelReport').toString()}
        </Heading>
        <Box>
          <Row>
            <Col md={4} xs={12} sm={12}>
              <StyledLabel htmlFor='numberOfBirth' id='numberOfBirthText'>
                {translation('numberOfBirthsQuestonText').toString()}
              </StyledLabel>
            </Col>
          </Row>
          <Row>
            <Col md={4} xs={12} sm={12}>
              <SelectInput field='numberOfBirth' options={numberOfBirthTypes} required={true} theme={themes.rebrand} data-testid='numberOfBirth' aria-labelledby='numberOfBirthText' initialValue='1' data-private={true}/>
            </Col>
          </Row>
          <Row>
            <Col md={4} xs={12} sm={12}>
              <StyledLabel htmlFor='deliveryType' id='deliveryTypeText'>
                {formState.values.numberOfBirth <= 1
            ? translation('deliveryTypeQuestionText').toString()
            : translation('deliveryTypeMultipleBirthsQuestionText').toString()}
              </StyledLabel>
            </Col>
          </Row>
          <Row style={{ marginBottom: '1rem' }}>
            <Col md={4} xs={12}>
              <SelectInput field='deliveryType' aria-labelledby='deliveryTypeText' semanticLabel={t(RabValidationLabels.deliveryType)} placeholderText={translation('birthTypeText') || ''} required={true} options={formState.values.numberOfBirth <= 1 ? deliveryTypes : deliveryMultipleTypes} theme={themes.rebrand} data-testid='deliveryType' validateOnChange={true} data-private={true}/>
            </Col>
          </Row>

          <Row>
            <Col md={4} xs={12} sm={12}>
              <StyledLabel htmlFor='dateOfBirth' id='dateOfBirthText'>
                {formState.values.numberOfBirth <= 1
            ? translation('dateOfBirthQuestionText').toString()
            : translation('dateOfMultipleBirthsQuestionText').toString()}
              </StyledLabel>
            </Col>
          </Row>
          <Row>
            <Col md={4} xs={12} sm={12}>
              <NewDateInput label={''} semanticLabel={t(CommonValidationLabels.dateOfBirth)} field='dateOfBirth' required={true} data-testid='dateOfBirth' minDate={parse(format(subYears(new Date(), 2), 'P'), 'P', new Date())} maxDate={parse(format(startOfToday(), 'P'), 'P', new Date())} validate={(value) => validateDateOfBirth(value)} theme={themes.rebrand} lang={language} mask={dateMask} data-private={true}/>
            </Col>
          </Row>
          <Row>
            <Col md={4} xs={12} sm={12}>
              <StyledLabel id='timeOfBirth' aria-labelledby='timeOfBirth'>
                {formState.values.numberOfBirth <= 1
            ? translation('timeOfBirthQuestionText').toString()
            : translation('timeOfMultipleBirthQuestionText').toString()}
              </StyledLabel>
            </Col>
          </Row>
          <Row>
            <Col md={2} xs={12} sm={12}>
              <SelectInput field='hours' label={t(RabValidationLabels.hour)} required={true} data-testid='hours' options={hoursTypes} theme={themes.rebrand} validateOnChange={true} aria-label='hours' data-private={true}/>
            </Col>
            <Col md={2} xs={12} sm={12}>
              <SelectInput field='minutes' label={t(RabValidationLabels.minute)} required={true} data-testid='minutes' options={createArrayOfHoursSelect(59)} theme={themes.rebrand} validateOnChange={true} aria-label='minutes' data-private={true}/>
            </Col>
            <Col md={2} xs={12} sm={12}>
              <SelectInput field={'ampm'} data-testid='ampm' options={amPmTypes} theme={themes.rebrand} initialValue='AM' required={true} label={'AM/PM'} placeholderText='AM' aria-label='am pm' data-private={true}/>
            </Col>
            <Col md={6} xs={12} sm={12}/>
          </Row>
          {validateTimeErrorText() !== '' ? (<Row>
              <Col>
                <InputMessage error={true} className='mt-0'>
                  {validateTimeErrorText()}
                </InputMessage>
              </Col>
            </Row>) : null}
          <Row>
            <Col md={4} xs={12} sm={12}>
              <StyledLabel id='lastDayWorkedText' htmlFor='lastDayWorked'>
                {translation('lastDayWorkedQuestionText').toString()}
              </StyledLabel>
            </Col>
          </Row>
          <Row>
            <Col md={4} xs={12} sm={12}>
              <NewDateInput label={''} semanticLabel={translation('lastDayWorkedText').toString()} field='lastDayWorked' data-testid='lastDayWorked' ariaLabelText='lastDayWorked' theme={themes.rebrand} lang={language} required={true} minDate={parse(format(subYears(new Date(), 2), 'P'), 'P', new Date())} maxDate={parse(format(startOfToday(), 'P'), 'P', new Date())} mask={dateMask} data-private={true}/>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={12}>
              <StyledLabel id='hospitalAdmissionDateLabel' htmlFor='hospitalAdmissionDate'>
                {translation('hospitalAdmissionDateQuestionText').toString()}
              </StyledLabel>
            </Col>
          </Row>
          <Row>
            <Col md={4} xs={12} sm={12}>
              <NewDateInput label='' aria-labelledby='hospitalAdmissionDateLabel' semanticLabel={translation('hospitalAdmissionDateText').toString()} field='hospitalAdmissionDate' data-testid='hospitalAdmissionDate' theme={themes.rebrand} minDate={parse(format(subYears(new Date(), 2), 'P'), 'P', new Date())} maxDate={parse(format(startOfToday(), 'P'), 'P', new Date())} mask={dateMask} optional={true} data-private={true}/>
            </Col>
          </Row>
          {formState.values.deliveryType === undefined || !props.rabCustomerConfData.displayBondingQuestion ? null : (<>
              <Row style={{ marginBottom: '0.5rem' }}>
                <Col xs={12} sm={12}>
                  <StyledLabel htmlFor='bondingQuestionType' optional={true}>
                    {bondingTypeText()}
                  </StyledLabel>
                </Col>
              </Row>
              <Row>
                <Col md={4} xs={12} sm={12}>
                  <SelectInput optional={true} semanticLabel={translation('bondingTimeText').toString()} data-testid='bondingQuestionType' field='bondingQuestionType' options={bondingQuestionTypes} theme={themes.rebrand} data-private={true}/>
                </Col>
              </Row>
            </>)}
        </Box>
        <Row>
          <Col>
            <Button aria-label={'Verify'} buttonType='primary' theme={themes.rebrand} role='button' id='verify' type='submit' data-testid='verifyButton'>
              {translation('verifyText').toString()}
            </Button>
          </Col>
        </Row>
      </div>
    </>);
};
const RabReport = (props) => {
    const stepper = useContext(StepperContext);
    const { trackEvent } = useTealium();
    const { user } = useAuthentication();
    const handleVerify = (values) => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.SELF_SERVICE,
            event_type: TEALIUM_EVENT.EVENT_TYPE.RAB,
            event_name: 'verify',
            event_version: TEALIUM_EVENT.EVENT_VERSION.REPORT,
            event_status: TEALIUM_EVENT.EVENT_STATUS.START,
            user_id: user?.webGUID
        });
        props.onComplete({
            numberOfBirth: values.numberOfBirth,
            deliveryType: values.deliveryType,
            dateOfBirth: values.dateOfBirth,
            hours: values.hours,
            minutes: values.minutes,
            ampm: values.ampm,
            lastDayWorked: values.lastDayWorked,
            hospitalAdmissionDate: values.hospitalAdmissionDate,
            bondingQuestionType: values.bondingQuestionType,
        });
        stepper.next();
    };
    return (<AccessibleForm onSubmit={handleVerify}>
      <RabReportForm rabCustomerConfData={props.rabCustomerConfData}/>
    </AccessibleForm>);
};
export default RabReport;
